import React, { useState } from "react";
import { Lock, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";

function Section({ title, children, testid }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid={testid}>
      <h3 className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

function Toggle({ label, desc, checked, onChange, disabled, testid }) {
  return (
    <label className={`flex items-start justify-between gap-4 rounded-xl border border-border px-4 py-3 ${disabled ? "opacity-80" : "hover:bg-muted/40"}`}>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-foreground">{label}</span>
        {desc && <span className="block text-xs text-muted-foreground">{desc}</span>}
      </span>
      <Switch checked={!!checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
    </label>
  );
}

export default function SesSettings({ cfg, onSaved }) {
  const { t } = useLang();
  const ro = !cfg.can_edit;
  const [f, setF] = useState({ ...cfg, test_password: "", live_password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setEv = (k, v) => setF((s) => ({ ...s, events: { ...s.events, [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, max_batch: Number(f.max_batch) || 100, test_password: f.test_password || undefined, live_password: f.live_password || undefined };
      delete body.endpoints; delete body.can_edit; delete body.has_test_password; delete body.has_live_password; delete body.updated_at; delete body.updated_by;
      const { data } = await api.put("/interfaces/ses/settings", body);
      onSaved(data); setF({ ...data, test_password: "", live_password: "" }); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const field = (k, label, extra = {}) => (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} disabled={ro} className="font-mono" data-testid={`ses-${k}`} {...extra} />
    </div>
  );
  const pw = (env) => (
    <div className="space-y-1.5">
      <Label className="text-xs flex items-center gap-2">{t("ses_password")}
        {cfg[`has_${env}_password`] && <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold">{t("ses_pw_set")}</span>}
      </Label>
      <Input type="password" autoComplete="new-password" value={f[`${env}_password`]} onChange={(e) => set(`${env}_password`, e.target.value)} disabled={ro}
        placeholder={cfg[`has_${env}_password`] ? "••••••••••" : ""} data-testid={`ses-${env}_password`} />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="ses-settings">
      {ro && <p className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" data-testid="ses-readonly"><Lock className="h-4 w-4" />{t("ses_readonly")}</p>}

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title={t("ses_mode")} testid="ses-sec-mode">
          <Toggle label={t("ses_enabled")} checked={f.enabled} onChange={(v) => set("enabled", v)} disabled={ro} testid="ses-enabled" />
          <div className="grid grid-cols-2 gap-2" role="radiogroup">
            {["test", "live"].map((m) => (
              <button key={m} type="button" disabled={ro} onClick={() => set("mode", m)} data-testid={`ses-mode-${m}`} aria-checked={f.mode === m} role="radio"
                className={`rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-colors ${f.mode === m
                  ? (m === "live" ? "border-red-500 bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200" : "border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200")
                  : "border-border text-muted-foreground hover:bg-muted/40"}`}>
                {t(`ses_mode_${m}`)}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{t("ses_mode_hint")}</p>
        </Section>

        <Section title={t("ses_codes")} testid="ses-sec-codes">
          <div className="grid gap-3 sm:grid-cols-2">
            {field("codigo_arrendador", t("ses_arrendador"), { maxLength: 10 })}
            {field("codigo_establecimiento", t("ses_establecimiento"), { maxLength: 10 })}
          </div>
          {field("aplicacion", t("ses_aplicacion"), { maxLength: 50 })}
        </Section>

        <Section title={t("ses_creds_test")} testid="ses-sec-test">
          <div className="grid gap-3 sm:grid-cols-2">{field("test_user", t("ses_user"))}{pw("test")}</div>
          <p className="text-[11px] font-mono text-muted-foreground break-all">{cfg.endpoints?.test}</p>
        </Section>
        <Section title={t("ses_creds_live")} testid="ses-sec-live">
          <div className="grid gap-3 sm:grid-cols-2">{field("live_user", t("ses_user"))}{pw("live")}</div>
          <p className="text-[11px] font-mono text-muted-foreground break-all">{cfg.endpoints?.live}</p>
        </Section>

        <Section title={t("ses_events")} testid="ses-sec-events">
          <Toggle label={t("ses_ev_rh")} desc={t("ses_ev_rh_desc")} checked={f.events?.rh} onChange={(v) => setEv("rh", v)} disabled={ro} testid="ses-ev-rh" />
          <Toggle label={t("ses_ev_pv")} desc={t("ses_ev_pv_desc")} checked={f.events?.pv} onChange={(v) => setEv("pv", v)} disabled={ro} testid="ses-ev-pv" />
          <Toggle label={t("ses_ev_cancel")} desc={t("ses_ev_cancel_desc")} checked={f.events?.cancel} onChange={(v) => setEv("cancel", v)} disabled={ro} testid="ses-ev-cancel" />
        </Section>

        <Section title={t("ses_sending")} testid="ses-sec-sending">
          <Toggle label={t("ses_auto_send")} checked={f.auto_send} onChange={(v) => set("auto_send", v)} disabled={ro} testid="ses-auto_send" />
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("ses_send_time")}</Label>
              <Input type="time" value={f.send_time || ""} onChange={(e) => set("send_time", e.target.value)} disabled={ro} className="font-mono" data-testid="ses-send_time" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("ses_send_interval")}</Label>
              <select value={String(f.send_interval_min ?? 0)} onChange={(e) => set("send_interval_min", Number(e.target.value))} disabled={ro} data-testid="ses-send_interval_min"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono">
                {[0, 15, 30, 60, 120, 240].map((m) => <option key={m} value={m}>{m === 0 ? t("ses_interval_0") : `${m} min`}</option>)}
              </select>
            </div>
            {field("max_batch", t("ses_max_batch"), { type: "number", min: 1, max: 100 })}
            {field("payment_default", t("ses_payment"), { maxLength: 5 })}
            {field("mail_to", t("ses_mail_to"), { type: "email", className: "" })}
          </div>
          <p className="text-xs text-muted-foreground">{t("ses_send_time_hint")} {t("ses_send_interval_hint")}</p>
        </Section>

        <Section title={t("ses_access")} testid="ses-sec-access">
          <Toggle label={t("ses_reception_files")} checked={f.reception_files} onChange={(v) => set("reception_files", v)} disabled={ro} testid="ses-reception_files" />
        </Section>
      </div>

      {!ro && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={busy} data-testid="ses-save"><Save className="h-4 w-4 mr-1.5" />{t("save")}</Button>
        </div>
      )}
    </div>
  );
}
